import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    SparklesIcon,
    ChartBarIcon,
    CheckCircleIcon,
    ArrowRightIcon,
    RocketLaunchIcon,
    DocumentTextIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';

const Demo = () => {
    const [selectedSector, setSelectedSector] = useState('tech');
    
    // Exemple de résultats d'analyse (simulé)
    const demoResults = {
        score: 78,
        sector: selectedSector,
        recommendations: [
            {
                category: 'Structure',
                priority: 'Haute',
                title: 'Optimiser la mise en page',
                description: 'Votre CV manque d\'espacement entre les sections. Ajoutez des marges pour améliorer la lisibilité.',
                impact: '+8 points'
            },
            {
                category: 'Mots-clés',
                priority: 'Moyenne',
                title: 'Ajouter des mots-clés sectoriels',
                description: 'Intégrez des termes techniques spécifiques à votre secteur pour passer les filtres ATS.',
                impact: '+5 points'
            },
            {
                category: 'Expérience',
                priority: 'Haute',
                title: 'Quantifier vos réalisations',
                description: 'Ajoutez des chiffres concrets : "Augmenté les ventes de 30%" au lieu de "Augmenté les ventes".',
                impact: '+10 points'
            }
        ],
        strengths: [
            'Formation solide et pertinente',
            'Expérience variée dans le secteur',
            'Compétences techniques bien présentées'
        ],
        improvements: [
            'Manque de mots-clés ATS',
            'Sections trop longues',
            'Absence de chiffres quantifiables'
        ]
    };

    const sectors = [
        { id: 'tech', name: 'Technologie', icon: '💻' },
        { id: 'finance', name: 'Finance', icon: '💰' },
        { id: 'marketing', name: 'Marketing', icon: '📊' },
        { id: 'sante', name: 'Santé', icon: '🏥' },
        { id: 'education', name: 'Éducation', icon: '📚' }
    ];

    return (
        <div className="page-shell">
            <Helmet>
                <title>Démonstration | CV Analyse — aperçu score et conseils</title>
                <meta name="description" content="Exemple d'aperçu CV Analyse : score et 3 conseils. Le rapport complet et le CV restructuré se débloquent dès 5 €." />
                <meta name="keywords" content="démo analyse CV, exemple analyse CV IA, CV Analyse" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://www.cv-analyse.fr/demo" />
                
                {/* Open Graph */}
                <meta property="og:title" content="Démonstration CV Analyse — aperçu score et conseils" />
                <meta property="og:description" content="Exemple d'aperçu : score et 3 conseils. Rapport complet dès 5 €." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.cv-analyse.fr/demo" />
                <meta property="og:image" content="https://www.cv-analyse.fr/og-image-1200x630.png" />
                
                {/* Schema.org pour la page démo */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "WebPage",
                  "name": "Démonstration CV Analyse",
                  "description": "Exemple d'aperçu : score et 3 conseils. Le rapport complet se débloque dès 5 €.",
                  "url": "https://www.cv-analyse.fr/demo",
                  "isPartOf": {
                    "@type": "WebSite",
                    "name": "CV Analyse",
                    "url": "https://www.cv-analyse.fr"
                  },
                  "about": {
                    "@type": "SoftwareApplication",
                    "name": "CV Analyse",
                    "applicationCategory": "BusinessApplication"
                  },
                  "breadcrumb": {
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                      {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Accueil",
                        "item": "https://www.cv-analyse.fr"
                      },
                      {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "Démonstration",
                        "item": "https://www.cv-analyse.fr/demo"
                      }
                    ]
                  }
                }
                `}</script>
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 py-12">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center mb-4">
                        <SparklesIcon className="w-8 h-8 text-blue-600 mr-3" />
                        <h1 className="text-3xl md:text-4xl font-semibold text-slate-900">
                            L&apos;aperçu offert, puis le rapport
                        </h1>
                    </div>
                    <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-6">
                        Exemple : score et 3 conseils, comme l&apos;aperçu sans inscription. Le détail, l&apos;ATS et le CV restructuré s&apos;ouvrent dès 5 €.
                    </p>
                    <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold mb-4">
                        <CheckCircleIcon className="w-5 h-5" />
                        <span>Aucune inscription requise pour cette démo</span>
                    </div>
                </div>

                {/* Sélecteur de secteur */}
                <div className="card-surface p-6 mb-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <ChartBarIcon className="w-6 h-6 mr-2 text-blue-600" />
                        Choisissez un secteur pour voir l'analyse personnalisée
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {sectors.map((sector) => (
                            <button
                                key={sector.id}
                                onClick={() => setSelectedSector(sector.id)}
                                className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                                    selectedSector === sector.id
                                        ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold'
                                        : 'border-gray-200 hover:border-blue-300 text-gray-700'
                                }`}
                            >
                                <div className="text-2xl mb-2">{sector.icon}</div>
                                <div className="text-sm font-medium">{sector.name}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Résultats de l'analyse démo */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    {/* Score principal */}
                    <div className="bg-slate-900 rounded-lg p-8 text-white">
                        <h2 className="text-2xl font-bold mb-6 flex items-center">
                            <ChartBarIcon className="w-6 h-6 mr-2" />
                            Score global
                        </h2>
                        <div className="text-center">
                            <div className="text-7xl font-bold mb-4">{demoResults.score}/100</div>
                            <div className="text-xl mb-6">Secteur : {sectors.find(s => s.id === selectedSector)?.name}</div>
                            <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                                <p className="text-sm mb-2">Potentiel d'amélioration</p>
                                <div className="text-2xl font-bold">+{100 - demoResults.score} points possibles</div>
                            </div>
                        </div>
                    </div>

                    <div className="card-surface p-6 flex flex-col justify-center">
                        <p className="text-sm font-semibold text-gray-500 mb-2">Inclus dès 5 €</p>
                        <h3 className="text-xl font-bold text-gray-800 mb-3">Dans le rapport complet</h3>
                        <ul className="space-y-2 text-gray-700 text-sm">
                            <li>Détail du score sur 5 critères</li>
                            <li>Points forts et toutes les recommandations</li>
                            <li>Mots-clés ATS</li>
                            <li>CV restructuré à copier</li>
                        </ul>
                    </div>
                </div>

                {/* Recommandations détaillées */}
                <div className="card-surface p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <SparklesIcon className="w-6 h-6 mr-2 text-blue-600" />
                        Les 3 conseils de l&apos;aperçu
                    </h2>
                    <div className="space-y-4">
                        {demoResults.recommendations.map((rec, index) => (
                            <div key={index} className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-all">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                rec.priority === 'Haute' 
                                                    ? 'bg-red-100 text-red-700' 
                                                    : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {rec.priority}
                                            </span>
                                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                                {rec.category}
                                            </span>
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800">{rec.title}</h3>
                                    </div>
                                    <div className="text-lg font-bold text-green-600">{rec.impact}</div>
                                </div>
                                <p className="text-gray-600">{rec.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Comparaison Gratuit vs Premium */}
                <div className="card-surface p-6 sm:p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                        Aperçu offert vs rapport dès 5 €
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Aperçu (offert)</h3>
                            <ul className="space-y-3">
                                <li className="flex items-center gap-2 text-gray-600">
                                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                    <span>Score global</span>
                                </li>
                                <li className="flex items-center gap-2 text-gray-600">
                                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                    <span>3 premiers conseils</span>
                                </li>
                            </ul>
                        </div>
                        <div className="bg-slate-900 rounded-lg p-6 text-white">
                            <h3 className="text-xl font-bold mb-4">Rapport + CV (5 €)</h3>
                            <ul className="space-y-3">
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5" />
                                    <span>Détail du score sur 5 critères</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5" />
                                    <span>Toutes les recommandations</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5" />
                                    <span>Mots-clés ATS</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircleIcon className="w-5 h-5" />
                                    <span>CV restructuré à copier</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* CTA Principal */}
                <div className="cta-band text-center mb-8">
                    <h2 className="text-2xl font-semibold mb-2">
                        Prêt à obtenir votre analyse ?
                    </h2>
                    <p className="text-slate-300 text-sm mb-5 max-w-xl mx-auto">
                        Score et 3 conseils offerts — rapport complet dès 5 €.
                    </p>
                    <Link to="/" className="btn-primary">
                        Voir mon score
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Demo;

